import { type JSX, useRef, useState } from 'react';
import clsx from 'clsx';
import { MdApartment, MdWork, MdLocationOn, MdOpenInNew } from 'react-icons/md';
import { ImDatabase } from 'react-icons/im';
import type AgentApiAiJobSearchRunsResult from '@/types/serializers/AgentApiAiJobSearchRunsResult';
import Checkbox from '@/components/Checkbox';
import SharedJobPostDetailModal from '@/features/agent/shared_job_posts/components/SharedJobPostDetailModal';
import toRecommendationView from '../lib/toRecommendationView';
import { truncateText } from '@/utils/truncateText';

type Props = {
  result: AgentApiAiJobSearchRunsResult;
  /** チェックボックスの選択状態 */
  isSelected: boolean;
  /** チェックボックスのトグルハンドラ */
  onToggleSelect: (resultId: number) => void;
};

/** AI推薦結果1件のカードコンポーネント */
export default function RecommendationCard({ result, isSelected, onToggleSelect }: Props) {
  const view = toRecommendationView(result);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const titleRef = useRef<HTMLParagraphElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // ホバー時にタイトルが省略されているかを判定して表示する（マウント時測定だと
  // モーダル展開前のレイアウトで判定が固定されてしまうため都度測定する）
  const handleTitleMouseEnter = () => {
    const el = titleRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      setShowTooltip(true);
    }
  };

  if (!view) return null;

  // 会社・職種・年収・勤務地を1行（折返し）に並べる（項目間に区切り線あり）
  const metaItems: { key: string; icon: JSX.Element; text: string; title?: string }[] = [];
  if (view.companyName) {
    metaItems.push({
      key: 'company',
      icon: <MdApartment size={20} className="text-main" aria-hidden="true" />,
      text: view.companyName,
    });
  }
  if (view.jobPositionName) {
    metaItems.push({
      key: 'position',
      icon: <MdWork size={20} className="text-main" aria-hidden="true" />,
      // ポジション名は長くなりがちなので10文字程度で切り詰め、全文は title で表示
      text: truncateText(view.jobPositionName),
      title: view.jobPositionName,
    });
  }
  if (view.salaryInfo) {
    metaItems.push({
      key: 'salary',
      icon: <ImDatabase size={18} className="text-main" aria-hidden="true" />,
      text: view.salaryInfo,
    });
  }
  if (view.workingLocation) {
    metaItems.push({
      key: 'location',
      icon: <MdLocationOn size={20} className="text-main" aria-hidden="true" />,
      text: view.workingLocation,
    });
  }

  return (
    <div className="rounded-sm bg-white py-lg pr-lg pl-md shadow-layer1">
      <div className="flex gap-1">
        {/* 左: チェックボックス */}
        <div className="flex size-8 items-center justify-center">
          <Checkbox checked={isSelected} onChange={() => onToggleSelect(result.id)} />
        </div>

        {/* 右: 内容領域 */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          {/* 行1: マッチ度 + 求人タイトル + 外部リンク */}
          <div className="flex h-8 items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-md">
              {result.match_score != null && (
                <span className="flex h-8 shrink-0 items-center rounded-sm bg-tag-blue px-sm text-xs leading-none font-bold text-tag-blue">
                  マッチ度 {result.match_score}%
                </span>
              )}
              <div className="relative min-w-0 flex-1">
                <p
                  ref={titleRef}
                  className="truncate text-base leading-[150%] font-bold text-main"
                  onMouseEnter={handleTitleMouseEnter}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  {view.title}
                </p>
                {showTooltip && (
                  <div className="absolute top-full left-0 z-10 mt-xs max-w-[540px] rounded-xs bg-black p-xs shadow-lg">
                    <div
                      className="absolute top-[-6px] left-6 h-2 w-3 bg-black"
                      style={{ clipPath: 'polygon(45.45% 0%, 0% 100%, 100% 100%)' }}
                    ></div>
                    <p className="text-xs leading-[170%] break-words text-white">{view.title}</p>
                  </div>
                )}
              </div>
            </div>
            {view.source === 'shared' && (
              <span className="flex h-6 shrink-0 items-center rounded-sm bg-gray px-sm text-xs font-bold text-main">
                求人DB
              </span>
            )}
            {view.source === 'shared' ? (
              <button
                type="button"
                onClick={() => setIsDetailOpen(true)}
                className="flex h-8 shrink-0 cursor-pointer items-center gap-1 rounded border-0 bg-transparent px-2 text-xs font-bold text-primary hover:underline"
              >
                求人詳細
                <MdOpenInNew size={12} aria-hidden="true" />
              </button>
            ) : (
              <a
                href={`/agent/provided_job_posts/${result.provided_job_post_id}`}
                target="_blank"
                rel="noreferrer"
                className="flex h-8 shrink-0 items-center gap-1 rounded px-2 text-xs font-bold text-primary hover:underline"
              >
                求人詳細
                <MdOpenInNew size={12} aria-hidden="true" />
              </a>
            )}
          </div>

          {/* 行2: 求人プロフィール情報（会社/職種/年収/勤務地を1行に折返し表示） */}
          {metaItems.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 text-xs text-main">
              {metaItems.map((item, index) => {
                const isLast = index === metaItems.length - 1;
                return (
                  <span
                    key={item.key}
                    className={clsx(
                      'flex items-center gap-2',
                      !isLast && 'border-0 border-r border-solid border-border pr-4'
                    )}
                  >
                    {item.icon}
                    <span className="leading-[1.7]" title={item.title}>
                      {item.text}
                    </span>
                  </span>
                );
              })}
            </div>
          )}

          {/* 行3: AI推薦理由 */}
          {result.match_reason && (
            <div className="flex flex-col gap-xxs rounded-sm bg-white p-sm shadow-layer1">
              <p className="text-xs leading-normal font-bold text-main">推薦理由</p>
              <p className="text-xs leading-[1.7] text-main">{result.match_reason}</p>
            </div>
          )}
        </div>
      </div>
      {result.shared_job_post && (
        <SharedJobPostDetailModal
          post={result.shared_job_post}
          open={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
        />
      )}
    </div>
  );
}
